// eslint-disable-next-line no-unused-vars
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment';

import schemaModule from '../modules/schema';
import intlModule from '../modules/intl';
import { getLogger } from '../logger';

import { toBoolean } from './utils';

const logger = getLogger('use-attributes');

const singleValueTypes = ['boolean', 'number', 'text', 'date'];

function isSingleValueType(type) {
  return singleValueTypes.includes(type);
}

function hasValues(attribute) {
  return !isSingleValueType(attribute.type) || attribute.values.length > 0;
}

function transformValues(values, language) {
  return values.map(entry => {
    const result = {
      ...entry,
      name: entry.value.find(value => value.lang === language || !value.lang).name,
    };
    delete result.value;
    return result;
  });
}

function transformAttributes(attributes = [], language) {
  return attributes.map(attribute => {
    const result = {
      id: attribute.id,
      isRequired: !!attribute.is_required,
      name: attribute.name.find(entry => entry.lang === language).name,
      type: attribute.type,
    };
    if (!hasValues(attribute)) {
      return result;
    }
    result.isMulti = attribute.type === 'checkbox';
    result.values = transformValues(attribute.values, language);
    result.original = attribute;
    return result;
  });
}

function getFromSource(attrsSource, attribute, defaultValue) {
  let result = defaultValue;
  const entry = attrsSource.attributes.find(entry => entry.id === attribute.id);
  if (entry && entry.values && entry.values.length > 0) {
    const { values } = entry;
    if (isSingleValueType(attribute.type)) {
      const value = values[0];
      if (value && value.value && value.value.length > 0) {
        switch (attribute.type) {
          case 'date':
            result = moment.utc(value.value[0].name).toDate();
            break;
          case 'boolean':
            result = toBoolean(value.value[0].name);
            break;
          case 'number':
            result = value.value[0].name;
            break;
          default:
            result = value.value[0].name;
        }
      } else {
        result = defaultValue;
      }
    } else if (attribute.isMulti) {
      result = values.map(entry => entry.id);
    } else {
      result = values[0].id;
    }
    logger.trace(
      'Get value Source',
      attrsSource,
      'AttributeId',
      attribute.id,
      'AttributeName',
      attribute.name,
      'Result',
      result,
      'DefaultValue',
      defaultValue
    );
  }
  return result;
}
function createGetter(attrsSource) {
  return (attribute, defaultValue) => {
    if (attrsSource && attrsSource.attributes && attribute) {
      return getFromSource(attrsSource, attribute, defaultValue);
    }
    return defaultValue;
  };
}

function createSetter(target, setAttrSource, originAttributes) {
  return attribute => newValue => {
    if (target && attribute && newValue != null) {
      const originAttribute = originAttributes.find(origin => origin.id === attribute.id);
      const previousValue = getFromSource(target, attribute, undefined);
      const entry = { id: originAttribute.id, name: originAttribute.name };
      if (isSingleValueType(attribute.type)) {
        switch (attribute.type) {
          case 'date':
            entry.values = [
              {
                value: [
                  {
                    name: moment(newValue)
                      .utc()
                      .toISOString(true),
                  },
                ],
              },
            ];
            break;
          case 'number':
            // eslint-disable-next-line no-case-declarations
            const newNumber = newValue && newValue.replace(',', '.');
            // eslint-disable-next-line no-restricted-globals
            if (isNaN(newNumber)) {
              entry.values = [{ value: [{ name: previousValue }] }];
            } else {
              entry.values = [{ value: [{ name: newNumber }] }];
            }
            break;
          case 'boolean':
            if (newValue) {
              entry.values = [{ value: [{ name: 'yes' }] }];
            } else {
              entry.values = [];
            }
            break;
          default:
            entry.values = [{ value: [{ name: newValue }] }];
        }
      } else if (attribute.isMulti) {
        entry.values = originAttribute.values.filter(value => newValue.find(entry => value.id === entry));
      } else {
        entry.values = originAttribute.values.filter(value => value.id === newValue);
      }
      logger.debug(
        'Set value to Target',
        target,
        'AttributeId',
        attribute.id,
        'AttributeName',
        attribute.name,
        'OriginAttribute',
        originAttribute,
        'Value',
        newValue,
        'Entry',
        entry
      );
      setAttrSource({
        attributes: [...target.attributes.filter(entry => entry.id !== attribute.id), entry],
      });
    }
  };
}

function useAttributes(source, metaId, category, language = 'en') {
  const dispatch = useDispatch();

  const intl = useSelector(intlModule.selectors.getIntl);
  const messages = useSelector(intlModule.selectors.getMessages);
  const attributes = useSelector(schemaModule.selectors.getAttributes);
  const isLoading = useSelector(schemaModule.selectors.isPending);
  const schemaDescriptors = useSelector(schemaModule.selectors.getSchemaDescriptors);
  const [languageAttributes, setLanguageAttributes] = useState([]);
  const [errors, setErrors] = useState({});

  const [attrsSource, setAttrSource] = useState({
    attributes: source ? source.attributes : [],
  });

  useEffect(() => {
    // Ensure meta data descriptor for the given metaId
    if ((!schemaDescriptors[metaId] || !schemaDescriptors[metaId].data) && !isLoading) {
      logger.trace('Fetch meta data for metaId:', metaId);
      dispatch(schemaModule.actions.loadMetadata({ metaId }));
    }
  }, [metaId, isLoading]);

  useEffect(() => {
    if (
      !attributes[category] ||
      (attributes[category] && attributes[category].list == null && !attributes[category].pending)
    ) {
      logger.debug('Load attributes on category', category);
      dispatch(schemaModule.actions.loadAttributes({ category }));
    } else {
      setLanguageAttributes(transformAttributes(attributes[category] && attributes[category].list, language));
    }
  }, [attributes, language]);

  useEffect(() => {
    if (attrsSource != null) {
      validate();
    }
  }, [attrsSource, attributes]);

  const getValue = useCallback(createGetter(attrsSource), [attrsSource]);
  const setValue = useCallback(
    createSetter(attrsSource, setAttrSource, attributes[category] && attributes[category].list, intl),
    [attrsSource, setAttrSource, attributes]
  );

  const validate = useCallback(() => {
    if (!attributes[category] || !attributes[category].list) {
      return;
    }
    const attrs = attributes[category].list;
    const errors = attrs
      .filter(attr => attr.is_required)
      .reduce((errors, attr) => {
        if (getValue(attr) === undefined) {
          return {
            ...(errors || {}),
            [attr.id]: {
              valid: false,
              rule: 'required',
              helpText: messages.required_message,
            },
          };
        }
        return errors;
      }, {});

    setErrors(errors);

    return errors;
  });

  return {
    attrsSource,
    attributes: languageAttributes,
    getValue,
    setValue,
    isLoading,
    errors,
    validate,
    setAttrSource,
  };
}

export default useAttributes;
